import { useRef, useState, useEffect } from 'react'
import { TextField, Button, Typography, Stack, Grid, IconButton, Box, Table, TableHead, TableBody, TableRow, TableCell, TablePagination, Checkbox } from '@mui/material'
import { AddCircleRounded, DeleteOutlineRounded, ArrowLeftRounded, ArrowRightRounded, KeyboardArrowLeftRounded, KeyboardArrowRightRounded } from '@mui/icons-material'
import { apiGet, apiPost } from '../Api_Functions'
import SmartDatePicker from '../smartDatePicker/Index'
import { SmartDialog, SmartDeleteDialog } from '../utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../systemValues/systemValueLabel'
import SmartSelect from '../smartSelect/SmartSelect'

// The step-2 list is an in-memory draft (never fetched): it paginates by slicing the local array,
// shown with the standard MIC table footer pager (<TablePagination> + the same four custom arrows
// as SmartMaterialUITablePagination) — NOT the server-side usePagination hook.
const DEFAULT_ROWS_PER_PAGE = 10
const ROWS_PER_PAGE_OPTIONS = [3, 5, 10, 50, 75, 100]

// The four-arrow actions (first/prev/next/last) that every MIC table footer renders.
const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1)
  return (
    <Stack direction='row' justifyContent='center' alignItems='center' sx={{ marginLeft: (theme) => theme.spacing(2.5) }}>
      <IconButton onClick={(event) => onPageChange(event, 0)} disabled={page === 0} aria-label='first page'>
        <ArrowLeftRounded />
      </IconButton>
      <IconButton onClick={(event) => onPageChange(event, page - 1)} disabled={page === 0} aria-label='previous page'>
        <KeyboardArrowLeftRounded />
      </IconButton>
      <IconButton onClick={(event) => onPageChange(event, page + 1)} disabled={page >= lastPage} aria-label='next page'>
        <KeyboardArrowRightRounded />
      </IconButton>
      <IconButton onClick={(event) => onPageChange(event, lastPage)} disabled={page >= lastPage} aria-label='last page'>
        <ArrowRightRounded />
      </IconButton>
    </Stack>
  )
}

const AddRawMaterialElement = (props) => {
  const { row, disable, onAlert, title } = props
  const { t_ } = useSmartTranslation()

  const [showDialog, setShowDialog] = useState(false)
  const [step, setStep] = useState('main')
  const [rawMaterialStatuses, setRawMaterialStatuses] = useState([])
  const [status, setStatus] = useState(null)
  const [instanceCount, setInstanceCount] = useState('1')
  const [instances, setInstances] = useState([])
  // 0-based visible page of the step-2 draft (RN5). Only this window gets rendered.
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
  // Ids of the lots ticked for deletion (non-unitary): standard select + DELETE pattern,
  // mirroring SmartMateriaUITable's multipleSelection + SmartMateriaUIToolbar's delete action.
  const [selectedIds, setSelectedIds] = useState([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const entryDate = useRef(new Date())
  // Local ids for manually added records (RN3). A ref counter instead of crypto.randomUUID():
  // the CRA test environment (jest-environment-jsdom 27 over jsdom 16.7) has no WebCrypto —
  // see the guard in generic/utilities/session_value_utils.js.
  const nextRecordId = useRef(1)

  useEffect(() => {
    apiGet('rawMaterials/statuses').then(data => {
      setRawMaterialStatuses(data || [])
      setStatus(data && data.length > 0 ? data[0] : null)
    })
  }, [])

  const isValidStatus = () => status !== null && status !== '' && status !== undefined

  // The "Instances", "Quantity" and "Size" inputs keep whatever the user types (including
  // a transient empty value) while editing: RN1/RN2 defer obligatoriness/minimum validation
  // to the moment of confirming, instead of coercing the empty state on every keystroke.
  //
  // "Instances" is always an integer field (RN7): a typed decimal (e.g. "2.5") must be rejected
  // as invalid rather than silently truncated by parseInt.
  //
  // "Quantity" (the amount contained in the instance) is an integer field too, EXCEPT when the
  // reference is fractionable and non-unitary (#76, RN1/D-e): a continuous-measure material
  // (coils, sheets, liquids) then accepts decimals with scale 3 (RN3) — the same gate "Size"
  // already uses. For a non-fractionable or unitary reference the quantity stays integer
  // (unchanged), and unitary keeps its {0,1} availability elsewhere.
  const isIntegerString = (value) => value !== '' && Number.isInteger(Number(value))

  // #76 gate (D-e): decimals for the per-instance quantity are enabled ONLY in this case.
  const acceptsDecimalQuantity = row.fractionable && !row.unitary

  // RN3 (#76): round to 3 decimals (same scale as size/qASU); mirrors RawMaterial.jsx's roundTo3.
  const roundTo3 = (value) => Math.round(value * 1000) / 1000

  const isInstanceCountValid = () => {
    if (!isIntegerString(instanceCount)) return false
    return parseInt(instanceCount, 10) >= 1
  }

  // Quantity parses as a float only on the fractionable non-unitary path; everywhere else it
  // stays parseInt so a stray decimal is truncated/rejected exactly as before (#71 RN7).
  const parseQuantity = (value) => acceptsDecimalQuantity ? parseFloat(value) : parseInt(value, 10)
  const parseSize = (value) => parseFloat(value)

  // The backend reads fractionable/unitary from the reference (by referenceCode).
  // The front only fills quantity on non-unitary references (lots) and size on
  // fractionable ones, and validates them before sending so the user gets a clear
  // error instead of a backend rejection or an incomplete record.
  //
  // #76: on the fractionable non-unitary path only the integer check (isIntegerString) is lifted
  // so a decimal is accepted; the "> 0" check (rejects negatives, and 0 as today) is preserved.
  const isQuantityValid = (item) => {
    if (row.unitary) return true
    if (acceptsDecimalQuantity) return parseQuantity(item.quantity) > 0
    return isIntegerString(item.quantity) && parseQuantity(item.quantity) > 0
  }
  const isSizeValid = (item) => !row.fractionable || parseSize(item.size) > 0

  const isInstancesValid = () => instances.every((item) => isQuantityValid(item) && isSizeValid(item))

  const visibleInstances = instances.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
  const allSelected = instances.length > 0 && selectedIds.length === instances.length
  const someSelected = selectedIds.length > 0 && !allSelected

  const handleNext = () => {
    if (!isInstanceCountValid()) return
    if (isNaN(entryDate.current)) return
    if (!isValidStatus()) return

    const count = parseInt(instanceCount, 10)
    setInstances(
      Array.from({ length: count }, (_, idx) => ({
        ...row,
        id: idx + 1,
        status,
        batch: '',
        quantity: 1,
        size: row.fractionable ? (row.size || 0) : 0
      }))
    )
    setPage(0) // D5/RN12: regenerating the draft always lands on the first page
    setStep('sizes')
  }

  const handleSizeChange = (idx, value) => {
    setInstances(prev => prev.map((item, i) => i === idx ? { ...item, size: value } : item))
  }

  // #76/RN3 (D-a): while the typed value still has ≤3 decimals keep it verbatim (so a partial
  // "12." or a trailing separator is never clobbered mid-typing); once it exceeds 3 decimals,
  // ROUND to 3 in the UI (no rejection). Only on the fractionable non-unitary path.
  const clampQuantityDecimals = (value) => {
    if (!acceptsDecimalQuantity || value === '' || value === '-') return value
    const dotIndex = value.indexOf('.')
    if (dotIndex === -1 || value.length - dotIndex - 1 <= 3) return value
    return String(roundTo3(Number(value)))
  }

  const handleQuantityChange = (idx, value) => {
    const nextValue = clampQuantityDecimals(value)
    setInstances(prev => prev.map((item, i) => i === idx ? { ...item, quantity: nextValue } : item))
  }

  const handleBatchChange = (idx, value) => {
    setInstances(prev => prev.map((item, i) => i === idx ? { ...item, batch: value } : item))
  }

  const handleStatusChange = (idx, newStatus) => {
    setInstances(prev => prev.map((item, i) => i === idx ? { ...item, status: newStatus } : item))
  }

  // A blank editable lot cloning the draft shape of handleNext, with the header status as its
  // default (D2). buildInstanceRequest stays intact, so the POST body is byte-for-byte the current one.
  const buildDraftRecord = () => ({
    ...row,
    id: 'record-' + nextRecordId.current++,
    status,
    batch: '',
    quantity: 1,
    size: row.fractionable ? (row.size || 0) : 0
  })

  // RN3 (non-unitary only): append one editable record (a lot).
  const handleAddRecord = () => {
    const nextInstances = [...instances, buildDraftRecord()]
    setInstances(nextInstances)
    // RN11/D4: jump to the last page so the freshly added record is visible.
    setPage(Math.ceil(nextInstances.length / rowsPerPage) - 1)
  }

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : instances.map((item) => item.id))
  }

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id])
  }

  // RN11/D4 (non-unitary only): deleting the ticked lots keeps the current page and only steps
  // back when the page empties — never below the first page. Confirmed via the standard
  // SmartDeleteDialog ("Delete data? … can't undo"), same as every delete in the MIC.
  const handleDeleteSelected = () => {
    const nextInstances = instances.filter((item) => !selectedIds.includes(item.id))
    setInstances(nextInstances)
    setSelectedIds([])
    setDeleteDialogOpen(false)
    const lastPage = Math.max(0, Math.ceil(nextInstances.length / rowsPerPage) - 1)
    if (page > lastPage) {
      setPage(lastPage)
    }
  }

  const selectedRows = instances.filter((item) => selectedIds.includes(item.id))
  const deleteColumns = [
    { name: t_('Name'), field: 'name' },
    { name: t_('Quantity'), field: 'quantity' },
    { name: t_('Batch'), field: 'batch' },
    { name: t_('Status'), field: 'status', render: (instance) => systemValueLabel(t_, 'RawMaterialStatus', instance.status || ''), renderOnlyInReadMode: true }
  ]

  const buildInstanceRequest = ({ size, quantity, status: s, batch }) => {
    const instance = { status: s }
    if (!row.unitary) instance.quantity = parseQuantity(quantity)
    if (row.fractionable) instance.size = parseSize(size)
    if (batch) instance.batch = batch
    return instance
  }

  const handleConfirm = () => {
    // RN9 + RN6/RN7 defense in depth: no POST with an empty draft or any invalid row on any page.
    if (instances.length === 0 || !isInstancesValid()) return
    // S30 (B-71-1): the non-unitary flow skips step 1, whose Next used to guard the Entry Date —
    // mirror it here so an invalid date can never confirm (would serialize as a null entryDate).
    if (isNaN(entryDate.current)) return
    setShowDialog(false)
    apiPost('rawMaterials/instances', {
      referenceCode: row.code,
      entryDate: entryDate.current.getTime(),
      instances: instances.map(buildInstanceRequest)
    }).catch(err => {
      onAlert({ title: t_('Error creating instances'), severity: 'error', message: t_('Instances could not be created') + ': ' + err })
    })
  }

  const onOpen = () => {
    entryDate.current = new Date()
    setInstanceCount('1')
    setPage(0)
    // RN1: the reference's unitary flag decides the wizard mode. A non-unitary reference
    // skips step 1 and starts a fresh empty draft; Entry Date and the default Status live
    // in the step-2 header (I1/RN4). Reopening always regenerates the draft (same policy
    // as the unitary D5 regeneration).
    setSelectedIds([])
    if (row.unitary) {
      setStep('main')
    } else {
      // Non-unitary opens straight on step 2 with one editable lot already added, ready to fill.
      setInstances([buildDraftRecord()])
      setStep('sizes')
    }
    setShowDialog(true)
  }

  const dialogTitle = {
    icon: <AddCircleRounded color='primary' sx={{ fontSize: '1.5em' }} />,
    render: (
      <Stack direction='row' justifyContent='center' alignItems='center'>
        <Typography variant='h6' color='primary'>{row.name}</Typography>
      </Stack>
    )
  }

  const renderStep1 = (
    <Grid container justifyContent='center' sx={{ padding: (theme) => theme.spacing(2) }} spacing={1}>
      <Grid item xs={2}>
        <TextField
          error={!isInstanceCountValid()}
          label={t_('Instances')}
          value={instanceCount}
          type='number'
          inputProps={{ min: 1, step: 1 }}
          onChange={(event) => setInstanceCount(event.target.value)}
        />
      </Grid>
      <Grid item xs={6}>
        <SmartDatePicker resultDate={entryDate} />
      </Grid>
      <Grid item xs={4}>
        <SmartSelect
          value={status}
          selectableOptions={rawMaterialStatuses}
          onChange={(newValue) => setStatus(newValue)}
          label={t_('Status')}
        />
      </Grid>
    </Grid>
  )

  const renderStep2 = (
    <Stack spacing={1} sx={{ padding: (theme) => theme.spacing(1) }}>
      {!row.unitary && (
        <Stack direction='row' spacing={1} alignItems='center'>
          <Box sx={{ width: '14rem' }}>
            <SmartDatePicker resultDate={entryDate} />
          </Box>
          <Box sx={{ width: '12rem' }}>
            <SmartSelect
              value={status}
              selectableOptions={rawMaterialStatuses}
              onChange={(newValue) => setStatus(newValue)}
              label={t_('Status')}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {selectedIds.length > 0 && (
            <Button
              sx={{ borderColor: 'transparent' }}
              color='error'
              variant='outlined'
              onClick={() => setDeleteDialogOpen(true)}
            >
              <DeleteOutlineRounded sx={{ marginRight: '0.5em' }} color='error' />
              <Typography color='error'>{t_('DELETE')}</Typography>
            </Button>
          )}
          <Button
            sx={{ borderColor: 'transparent' }}
            color='primary'
            variant='outlined'
            disabled={!isValidStatus()}
            onClick={handleAddRecord}
          >
            <AddCircleRounded sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('Add Record')}</Typography>
          </Button>
        </Stack>
      )}
      <Box sx={{ overflowX: 'auto', overflowY: 'auto', height: '26rem' }}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              {!row.unitary && (
                <TableCell padding='checkbox'>
                  <Checkbox
                    color='primary'
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
              )}
              <TableCell>{t_('Instance')}</TableCell>
              <TableCell>{t_('Name')}</TableCell>
              <TableCell>{t_('Material type')}</TableCell>
              {!row.unitary && <TableCell>{t_('Quantity')}</TableCell>}
              {row.fractionable && <TableCell>{t_('Size')}</TableCell>}
              <TableCell>{t_('Unit')}</TableCell>
              <TableCell>{t_('Batch')}</TableCell>
              <TableCell>{t_('Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleInstances.map((item, visibleIndex) => {
              const index = page * rowsPerPage + visibleIndex
              return (
                <TableRow key={item.id} selected={selectedIds.includes(item.id)}>
                  {!row.unitary && (
                    <TableCell padding='checkbox'>
                      <Checkbox
                        color='primary'
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelectRow(item.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{systemValueLabel(t_, 'material_type', item.materialType)}</TableCell>
                  {!row.unitary && (
                    <TableCell sx={{ width: '8rem' }}>
                      <TextField
                        error={!isQuantityValid(item)}
                        value={item.quantity}
                        type='number'
                        size='small'
                        inputProps={acceptsDecimalQuantity ? { step: 0.001, min: 0 } : { step: 1, min: 1 }}
                        onChange={(event) => handleQuantityChange(index, event.target.value)}
                      />
                    </TableCell>
                  )}
                  {row.fractionable && (
                    <TableCell sx={{ width: '8rem' }}>
                      <TextField
                        error={!isSizeValid(item)}
                        value={item.size}
                        type='number'
                        size='small'
                        inputProps={{ step: 0.01, min: 0 }}
                        onChange={(event) => handleSizeChange(index, event.target.value)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{item.unit?.code}</TableCell>
                  <TableCell sx={{ width: '8rem' }}>
                    <TextField
                      value={item.batch}
                      size='small'
                      onChange={(event) => handleBatchChange(index, event.target.value)}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '12rem' }}>
                    <SmartSelect
                      value={item.status}
                      size='small'
                      selectableOptions={rawMaterialStatuses}
                      onChange={(newStatus) => handleStatusChange(index, newStatus)}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Box>
      <TablePagination
        component='div'
        sx={{ borderTop: '1px solid rgba(81, 81, 81, 1)', backgroundColor: '#373636 !important' }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={instances.length}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0) }}
        ActionsComponent={TablePaginationActions}
        labelRowsPerPage={t_('Rows per page:')}
      />
    </Stack>
  )

  const buttonsStep1 = (
    <Button variant='contained' onClick={handleNext} disabled={!isInstanceCountValid() || !isValidStatus()}>
      {t_('Next')}
    </Button>
  )

  const buttonsStep2 = (
    <Stack direction='row' spacing={1}>
      {row.unitary && <Button onClick={() => setStep('main')}>{t_('Back')}</Button>}
      <Button variant='contained' onClick={handleConfirm} disabled={instances.length === 0 || !isInstancesValid()}>{t_('Accept')}</Button>
    </Stack>
  )

  return (
    <div>
      <SmartDialog
        setOpen={showDialog}
        accept={false}
        cancel
        cancelCallback={() => setShowDialog(false)}
        title={dialogTitle}
        renderComponent={step === 'main' ? renderStep1 : renderStep2}
        renderCustomButtons={step === 'main' ? buttonsStep1 : buttonsStep2}
        paperWidth={step === 'sizes' ? '70rem' : undefined}
      />
      <SmartDeleteDialog
        show={deleteDialogOpen}
        cancelCallback={() => setDeleteDialogOpen(false)}
        deleteCallBack={handleDeleteSelected}
        rows={selectedRows}
        columns={deleteColumns}
      />
      {title === undefined
        ? (
          <IconButton color='primary' onClick={onOpen} disabled={disable}>
            <AddCircleRounded />
          </IconButton>
          )
        : (
          <Button
            sx={{ borderColor: 'transparent' }}
            color='primary'
            variant='outlined'
            onClick={onOpen}
            disabled={disable}
          >
            <AddCircleRounded style={{ fontSize: '1.2em' }} />
            <Typography style={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{title}</Typography>
          </Button>
          )}
    </div>
  )
}

export default AddRawMaterialElement
